import * as React from 'react';
import arrayMove from 'array-move';
import { connect } from 'react-redux';
import { capitalize, differenceWith, isNil } from 'lodash';
import classNames from 'classnames';

import { __t, applyTranslations } from 'utils/language';

import { Form, FormField } from 'components/form';
import RealmOutfitsImages from '../realm-outfits-images/RealmOutfitsImages';
import Input from 'components/inputs/input/Input';
import RealmOutfitVideo from '../realm-outfit-video/RealmOutfitVideo';
import OutfitItems from '../outfit-items/OutfitItems';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import OutfitReviewHeader from '../outfit-review-header/OutfitReviewHeader';
import { PromptExit } from 'libs/router';
import ActionButton from 'components/buttons/action-button/ActionButton';
import StatusLabel from 'components/status-label/StatusLabel';
import OutfitReviewInfo from '../outfit-review-info/OutfitReviewInfo';
import { withModal } from 'components/modal/ModalHOC';
import PromptModal from 'components/modal/prompt-modal/PromptModal';
import OutfitDeclineHistory from '../outfit-decline-history/OutfitDeclineHistory';

import { isMultipleItemsInArrayRequired, isRequired, maxLengthArray } from 'utils/validators';
import { isBlobUrl } from 'utils/url';
import {
  CAN_PUBLISH_STATUSES,
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_PUBLISHED,
  hasItemWithSameSKU,
  splitTagsByType
} from 'utils/realm-outfits';
import { isCcmModerator } from 'utils/account';

import { downloadFileIndividually } from 'modules/realm-outfits/reducers/realm-outfits';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchRealmOutfitsTags } from 'modules/realm-outfits/reducers/realm-outfits-tags';
import { danger } from 'redux-store/reducers/common-reducer';
import {
  declineOutfit,
  publishUnpublishOutfit,
  submitCreatorlessOutfits
} from 'modules/product-list/reducers/campaign-outfits/campaign-outfits';

import './outfit-create-modal.scss';

import { MixedSelectDropdownItem, Role } from 'types';
import {
  RealmOutfit,
  RealmOutfitBrandForm,
  RealmOutfitImage,
  RealmOutfitItemResource,
  RealmOutfitTag
} from 'modules/realm-outfits/types/RealmOutfit.type';

applyTranslations(require('./outfit-create-modal.i18n.json'));

const MSG_KEY = 'outfit_create_modal';

type Image = Omit<RealmOutfitImage, 'sortOrder'>

type State = {
  isVideoUploading: boolean,
  canSortImages: boolean,
  outfit: RealmOutfitBrandForm,
  addNewItem: boolean,
  outfitPosition: number | null,
  isModified: boolean,
  mode: 'edit' | 'create' | 'view',
  addedSKUs: Array<RealmOutfitItemResource>,
  removedSKUIds: Array<number>
};

interface OwnProps {
  genders: Array<string>,
  mode: 'create' | 'view',
  campaignName: string,
  campaignId: number,
  outfitPosition: number,
  onCloseModal: () => void,
  minSKUs?: number,
  maxSKUs?: number
}

interface StateProps {
  outfits: Array<RealmOutfit & { deliverableId: number }>,
  realmOutfitsTags: Array<RealmOutfitTag>,
  campaignDeliverableId: number | null,
  roles: Array<Role>
}

interface DispatchProps {
  dispatchDanger: (msg: string) => void,
  dispatchFetchRealmOutfitsTags: () => void,
  dispatchSubmitOutfit: (
    campaignId: number,
    deliverableId: number,
    data: Omit<RealmOutfitBrandForm, 'id'>,
    addedSKUs: Array<RealmOutfitItemResource>,
    removedSKUIds: Array<number>,
    outfitId?: number
  ) => Promise<boolean>,
  dispatchPublishUnpublishOutfit:
    (campaignId: number, outfitId: number, deliverableId: number, type?: 'publish' | 'unpublish') => Promise<boolean>,
  dispatchDeclineOutfit: (campaignId: number, outfitId: number, deliverableId: number, reason: string) => Promise<boolean>,
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message: string,
      confirmButtonLabel: string,
      confirmButtonColor?: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchDownloadFileIndividually: (url: string) => void
}

type Props = OwnProps & StateProps & DispatchProps;

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: PromptModal
});

class OutfitCreateModal extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const { mode, outfits, outfitPosition } = props;

    const currentOutfit = mode === 'create' ? this.getEmptyOutfit() : outfits[outfitPosition];

    this.state = {
      mode,
      outfitPosition: outfitPosition || 0,
      isVideoUploading: false,
      outfit: {
        ...currentOutfit,
        tags: this.getSelectedGtlTags(currentOutfit)
      },
      canSortImages: false,
      addNewItem: false,
      isModified: false,
      addedSKUs: [],
      removedSKUIds: []
    };
  }

  componentDidMount() {
    const { dispatchFetchRealmOutfitsTags } = this.props;
    dispatchFetchRealmOutfitsTags();
  }

  getSelectedGtlTags(outfit: RealmOutfit) {
    return (outfit.tags || []).filter(t => t.type === 'gtl').map(t => t.id);
  }

  getEmptyOutfit() {
    return {
      id: Math.random(),
      name: '',
      items: [],
      images: [],
      video: null,
      tags: []
    };
  }

  getGtlTags = (): Array<MixedSelectDropdownItem> => {
    const { outfit } = this.state;
    const { genders, realmOutfitsTags } = this.props;

    const outfitTags = splitTagsByType(realmOutfitsTags);
    const outfitGender = genders.includes('men')
      ? 'men'
      : 'women';

    const gtlFilteredByGender = outfitTags.gtl.filter(
      t => isNil(t.genders) || (t.genders ?? []).length === 0
        || (t.genders ?? []).includes(outfitGender)
    );

    return gtlFilteredByGender.map(tag => ({
      label: capitalize(tag.title),
      value: tag.id,
      identifier: tag.id,
      isDisabled: false,
      isFocused: false,
      isSelected: outfit.tags.includes(tag.id)
    }));
  };

  handleFieldChange = (value: string, fieldType: string) => {
    this.setState(prevState => ({
      ...prevState,
      isModified: true,
      outfit: {
        ...prevState.outfit,
        [fieldType]: value
      }
    }));
  };

  handleOutfitImageReplaced = (image: Image, position: number): void => {
    this.setState(prevState => ({
      isModified: true,
      outfit: {
        ...prevState.outfit,
        images: [
          ...prevState.outfit.images.slice(0, position),
          image,
          ...prevState.outfit.images.slice(position + 1)
        ]
      }
    }));
  };

  handleOutfitImageUploaded = (images: Array<Image>): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        images: [
          ...state.outfit.images,
          ...images
        ]
      }
    }));
  };

  handleOutfitImageReordered = (oldIndex: number, newIndex: number): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        images: arrayMove(state.outfit.images, oldIndex, newIndex)
      }
    }));
  };

  handleOutfitImageDeleted = (index: number): void => this.setState(state => ({
    isModified: true,
    outfit: {
      ...state.outfit,
      images: [
        ...state.outfit.images.slice(0, index),
        ...state.outfit.images.slice(index + 1, state.outfit.images.length)
      ]
    }
  }));

  handleToggleSortingForOutfitImages = (canSortImages: boolean): void => this.setState(() => ({
    canSortImages
  }));

  handleAddOutfitVideo = (file: File): void => {
    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        video: {
          file,
          outfitId: state.outfit.id,
          url: URL.createObjectURL(file),
          audioType: null
        }
      }
    }));
  };

  handleDeleteOutfitVideo = (): void => {
    const blobUrl = this.state.outfit.video?.url;

    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        video: null
      }
    }), () => {
      if (blobUrl && isBlobUrl(blobUrl)) {
        URL.revokeObjectURL(blobUrl);
      }
    });
  };

  private handleAddItem = (item: RealmOutfitItemResource) => {
    const { dispatchDanger } = this.props;
    const { outfit } = this.state;

    if (hasItemWithSameSKU(outfit.items, item.sku)) {
      dispatchDanger(__t('realm_outfit_details_edit.items_with_same_sku', { sku: item.sku }));
      return;
    }

    this.setState(state => ({
      isModified: true,
      outfit: {
        ...state.outfit,
        items: [
          item,
          ...state.outfit.items
        ]
      },
      addedSKUs: [...state.addedSKUs, item]
    }));
  };

  handleDeleteItem = (deletedItemSKU: string, id: number) => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('outfit_create_modal.item_delete_confirmation.message'),
      confirmButtonLabel: __t('outfit_create_modal.item_delete_confirmation.button_confirm'),
      onConfirm: () => {
        this.setState(state => ({
          isModified: true,
          outfit: ({
            ...state.outfit,
            items: state.outfit.items.filter(item => item.sku !== deletedItemSKU)
          } as RealmOutfitBrandForm),
          removedSKUIds: [...state.removedSKUIds, id]
        }), () => dispatchHideConfirmationModal());
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  handleOutfitTagsChanged = (items: Array<MixedSelectDropdownItem>): void => {
    this.setState(state => {
      const tags = state.outfit.tags;
      const difference = differenceWith(tags, items.map(item => item.value));

      return ({
        isModified: true,
        outfit: {
          ...state.outfit,
          tags: [
            ...difference,
            ...items.filter(item => item.isSelected).map(item => item.value)
          ]
        }
      });
    });
  };

  handleCancel = () => {
    const { isModified } = this.state;
    const { onCloseModal, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    if (isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${MSG_KEY}.changes_modal.title`),
        message: __t(`${MSG_KEY}.changes_modal.message`),
        confirmButtonLabel: __t(`${MSG_KEY}.changes_modal.confirm_btn`),
        confirmButtonColor: 'primary',
        onConfirm: () => {
          dispatchHideConfirmationModal();
          onCloseModal();
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
        }
      });
    } else {
      onCloseModal();
    }
  };

  handleSubmitOutfit = async () => {
    const { outfit, mode, addedSKUs, removedSKUIds } = this.state;
    const { dispatchSubmitOutfit, onCloseModal, campaignDeliverableId, campaignId } = this.props;

    if (!campaignDeliverableId) {
      return;
    }

    const data = {
      ...outfit,
      images: outfit.images.map((image, index) => ({
        ...image,
        sortOrder: index
      }))
    };

    const isSubmitted = await dispatchSubmitOutfit(
      campaignId,
      campaignDeliverableId,
      data,
      addedSKUs,
      removedSKUIds,
      mode === 'edit' ? outfit.id : undefined
    );

    if (isSubmitted) {
      onCloseModal();
    }
  };

  handleDecline = async (declinedReason: string) => {
    const { campaignId, campaignDeliverableId, onCloseModal, dispatchDeclineOutfit } = this.props;
    const { outfit } = this.state;

    if (campaignDeliverableId) {
      const isDeclined = await dispatchDeclineOutfit(campaignId, outfit.id, campaignDeliverableId, declinedReason);

      if (isDeclined) {
        onCloseModal();
      }
    }
  };

  handlePublish = async () => {
    const {
      campaignId,
      campaignDeliverableId,
      onCloseModal,
      dispatchPublishUnpublishOutfit,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const { outfit } = this.state;

    dispatchShowConfirmationModal({
      message: __t(`${MSG_KEY}.confirmation_publish_message`),
      confirmButtonLabel: __t(`${MSG_KEY}.confirm_publish_btn`),
      onConfirm: async () => {
        if (campaignDeliverableId) {
          const isPublished = await dispatchPublishUnpublishOutfit(
            campaignId,
            outfit.id,
            campaignDeliverableId,
            'publish'
          );

          dispatchHideConfirmationModal();

          if (isPublished) {
            onCloseModal();
          }
        }
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  renderActions = (): JSX.Element => {
    const { mode, outfit, isModified } = this.state;

    if (mode === 'view' && outfit.status === 'declined') {
      return (
        <React.Fragment>
          <ActionButton
            color="tertiary"
            onClick={this.handleCancel}
          >
            {__t(`${MSG_KEY}.actions.close`)}
          </ActionButton>
          <ActionButton
            color="primary"
            type="button"
            onClick={() => {
              this.setState({
                mode: 'edit'
              });
            }}
          >
            {__t(`${MSG_KEY}.actions.edit_outfit`)}
          </ActionButton>
        </React.Fragment>
      );
    }

    if (mode === 'view') {
      return (
        <ActionButton
          color="tertiary"
          onClick={this.handleCancel}
        >
          {__t(`${MSG_KEY}.actions.close`)}
        </ActionButton>
      );
    }

    return (
      <React.Fragment>
        <ActionButton
          color="tertiary"
          onClick={this.handleCancel}
        >
          {__t(`${MSG_KEY}.actions.cancel`)}
        </ActionButton>
        <ActionButton
          color="primary"
          disabled={!isModified}
          type="submit"
        >
          {__t(`${MSG_KEY}.actions.submit`)}
        </ActionButton>
      </React.Fragment>
    );
  };

  renderRealmTags(tags: Array<string>) {
    const { realmOutfitsTags } = this.props;

    if (!tags || tags.length === 0) {
      return '—';
    }

    return (
      <React.Fragment>
        {
          tags.map(tag => {
            const title = realmOutfitsTags.find(t => t.id === tag)?.title;

            return (
              <span
                key={tag}
                className="realm-outfit-details__tag"
              >
                <StatusLabel
                  type="neutral"
                  size="small"
                  label={capitalize(title)}
                />
              </span>
            );
          })
        }
      </React.Fragment>
    );
  }

  renderActionButtons = (showDeclineHistory: boolean) => {
    const { mode, outfit } = this.state;
    const { roles } = this.props;
    const containerClasses = classNames('outfit-create-modal__buttons', {
      'outfit-create-modal__buttons--with-decline-history': showDeclineHistory
    });

    const isEditMode = mode === 'edit';
    const isModerator = isCcmModerator(roles);

    if (!isEditMode && isModerator && CAN_PUBLISH_STATUSES.includes(outfit.status)) {
      return (
        <div className={containerClasses}>
          {
            outfit.status === REALM_OUTFIT_STATUS_PENDING && (
              <ActionButtonWithModal
                color="secondary"
                modalContentProps={{
                  onSubmit: this.handleDecline,
                  title: __t(`${MSG_KEY}.confirmation_decline.message`),
                  label: __t(`${MSG_KEY}.confirmation_decline.reason_label`),
                  value: '',
                  multiline: true,
                  okBtnText: __t(`${MSG_KEY}.confirmation_decline.button`)
                }}
              >
                {__t(`${MSG_KEY}.actions.decline`)}
              </ActionButtonWithModal>
            )
          }
          <ActionButton
            color="primary"
            onClick={() => this.handlePublish()}
          >
            {__t(`${MSG_KEY}.actions.publish`)}
          </ActionButton>
        </div>
      );
    }

    return (
      <div className={containerClasses}>
        {this.renderActions()}
      </div>
    );
  };

  renderDeclineHistory = (showDeclineHistory: boolean) => {
    const { outfit } = this.state;

    if (showDeclineHistory) {
      return (
        <div className="outfit-create-modal__section">
          <div className="outfit-create-modal__heading">
            {__t('outfit_create_modal.declined_reason')}
          </div>
          <OutfitDeclineHistory declineHistories={outfit.declineHistories || []} />
        </div>
      );
    }

    return null;
  };

  render() {
    const { mode, isModified, outfitPosition, outfit, canSortImages } = this.state;
    const {
      outfits,
      campaignName,
      roles,
      minSKUs,
      maxSKUs,
      dispatchDownloadFileIndividually
    } = this.props;
    const isModerator = isCcmModerator(roles);

    const isPublished = outfit.status === REALM_OUTFIT_STATUS_PUBLISHED
          || outfit.status === REALM_OUTFIT_STATUS_APPROVED;
    const showDeclineHistory = !isPublished && Boolean(outfit?.declineHistories?.length);

    const gtlTags = this.getGtlTags();
    const isEditMode = mode !== 'view';

    const containerClass = classNames('outfit-create-modal__main', {
      'outfit-create-modal__main--view': !isEditMode
    });

    const minItems = minSKUs ?? 3;
    const maxItems = maxSKUs ?? 5;

    return (
      <div className={containerClass}>
        <OutfitReviewHeader
          title={mode === 'view' ? outfit?.name : __t(`${MSG_KEY}.${mode}_title`)}
          subTitle={mode === 'create' ? campaignName : ''}
          currentPosition={outfitPosition || 0}
          maxPosition={outfits.length - 1}
          isEditMode={isEditMode}
          status={outfit.status}
          onChangingCurrentPosition={(position) => {
            // To have animation effect, outfit position set to null
            this.setState({
              outfitPosition: null
            }, () => {
              this.setState({
                outfitPosition: position,
                outfit: {
                  ...outfits[position],
                  tags: this.getSelectedGtlTags(outfits[position])
                }
              });
            });
          }}
        />
        {
          !isEditMode && outfit && (
            <div className="outfit-create-modal__info">
              <OutfitReviewInfo
                creatorWmcLink={null}
                isBrandCreative
                onEditOutfit={isModerator ? () => {
                  this.setState({
                    mode: 'edit'
                  });
                } : undefined}
              // @ts-ignore outfit will be Realm Outfit
                outfit={outfit}
              />
            </div>
          )
        }
        <Form
          className="outfit-create-modal__form"
          onSubmit={this.handleSubmitOutfit}
        >
          {
            isModified &&
              <PromptExit
                type="modal"
                title={__t(`${MSG_KEY}.changes_modal.title`)}
                message={__t(`${MSG_KEY}.changes_modal.message`)}
                actionButtonLabel={__t(`${MSG_KEY}.changes_modal.confirm_btn`)}
                actionButtonColor="primary"
              />
          }
          {
            isEditMode && (
              <FormField
                label={__t(`${MSG_KEY}.name.title`)}
                sublabel={__t(`${MSG_KEY}.name.sub_title`)}
                fontSize="small"
                direction="column"
                labelClassName="outfit-create-modal__label"
                className="outfit-create-modal__name"
                showRequiredMark
                showValidationIcon={false}
                validators={[
                  isRequired(outfit.name)
                ]}
              >
                <Input
                  name="name"
                  placeholder={__t(`${MSG_KEY}.name.placeholder`)}
                  charCount={{
                    curCount: outfit.name.length,
                    maxChars: 25
                  }}
                  value={outfit.name}
                  onChange={(event) => this.handleFieldChange(event?.target?.value || '', 'name')}
                />
              </FormField>
            )
          }
          <FormField
            label={__t(`${MSG_KEY}.images.${mode === 'view' ? 'view_' : ''}title`)}
            sublabel={__t(`${MSG_KEY}.images.${mode === 'view' ? 'view_' : ''}sub_title`)}
            fontSize="small"
            direction="column"
            labelClassName="outfit-create-modal__label"
            showRequiredMark={isEditMode}
            showValidationIcon={false}
            validators={[
              isMultipleItemsInArrayRequired(outfit.images, __t(`${MSG_KEY}.images.error`), 2),
              maxLengthArray(outfit.images, 5, __t(`${MSG_KEY}.images.error`))
            ]}
          >
            <RealmOutfitsImages
              isInModal
              canUpload={isEditMode}
              canSortImages={canSortImages}
              images={outfit.images}
              onUpload={(images, position) => {
                if (position) {
                  this.handleOutfitImageReplaced(images[0], position);
                } else {
                  this.handleOutfitImageUploaded(images);
                }
              }}
              onDelete={this.handleOutfitImageDeleted}
              onImageReorder={this.handleOutfitImageReordered}
              onRearrangeOrder={this.handleToggleSortingForOutfitImages}
            />
          </FormField>
          <FormField
            label={__t(`${MSG_KEY}.video.${mode === 'view' ? 'view_' : ''}title`)}
            sublabel={__t(`${MSG_KEY}.video.${mode === 'view' ? 'view_' : ''}sub_title`)}
            fontSize="small"
            direction="column"
            labelClassName="outfit-create-modal__label"
            showValidationIcon={false}
          >
            <RealmOutfitVideo
              onAddOutfitVideo={(files) => this.handleAddOutfitVideo(files[0])}
              video={outfit.video}
              isEditMode={isEditMode}
              isInModal
              onDeleteVideo={this.handleDeleteOutfitVideo}
              onDownloadVideo={dispatchDownloadFileIndividually}
            />
          </FormField>
          <FormField
            label={__t(`${MSG_KEY}.sku_items.${mode === 'view' ? 'view_' : ''}title`)}
            sublabel={__t(`${MSG_KEY}.sku_items.${mode === 'view' ? 'view_' : ''}sub_title`)}
            fontSize="small"
            direction="column"
            labelClassName="outfit-create-modal__label"
            showRequiredMark={isEditMode}
            showValidationIcon={false}
            validators={[
              isMultipleItemsInArrayRequired(
                outfit.items,
                __t(`${MSG_KEY}.sku_items.error`),
                minItems - 1
              ),
              maxLengthArray(
                outfit.items,
                maxItems,
                __t(`${MSG_KEY}.sku_items.error`)
              )
            ]}
          >
            <OutfitItems
              editMode={(() => {
                if (!isEditMode) {
                  return 'none';
                }

                if (isEditMode && outfit.items.length === maxItems) {
                  return 'remove-only';
                }

                return 'edit';
              })()}
              onAddNew={this.handleAddItem}
              items={outfit.items}
              hasSimilarItem={false}
              onDeleteItem={(sku, id) => this.handleDeleteItem(sku, id)}
            />
          </FormField>
          <FormField
            label={__t(`${MSG_KEY}.tags.${mode === 'view' ? 'view_' : ''}title`)}
            sublabel={__t(`${MSG_KEY}.tags.${mode === 'view' ? 'view_' : ''}sub_title`)}
            fontSize="small"
            direction="column"
            labelClassName="outfit-create-modal__label"
            className="outfit-create-modal__gtl-tags"
            showRequiredMark={isEditMode}
            showValidationIcon={false}
            validators={[
              isRequired(outfit.tags)
            ]}
          >
            {
              isEditMode ? (
                <MixedSelectDropdown
                  name="tags"
                  placeholder={__t(`${MSG_KEY}.tags.placeholder`)}
                  withRemovable
                  className="realm-outfit-details-edit__compact-select"
                  items={gtlTags}
                  onItemsToggle={this.handleOutfitTagsChanged}
                  onItemsToggleAll={this.handleOutfitTagsChanged}
                  onItemsRemove={this.handleOutfitTagsChanged}
                  onItemsRemoveAll={this.handleOutfitTagsChanged}
                />
              ) : this.renderRealmTags(outfit.tags)
            }
          </FormField>
          {this.renderDeclineHistory(showDeclineHistory)}
          {this.renderActionButtons(showDeclineHistory)}
        </Form>
      </div>
    );
  }
}

const mapStateToProps = ({
  realmOutfitsTags,
  campaignOutfits,
  account
}): StateProps => ({
  realmOutfitsTags: realmOutfitsTags.tags,
  outfits: campaignOutfits.outfits || [],
  campaignDeliverableId: campaignOutfits.campaignDeliverableId,
  roles: account.roles
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDanger: (message) => dispatch(danger(message)),
  dispatchFetchRealmOutfitsTags: () => dispatch(fetchRealmOutfitsTags()),
  dispatchSubmitOutfit: (campaignId, deliverableId, data, added, removed, outfitId) =>
    dispatch(submitCreatorlessOutfits(campaignId, deliverableId, data, added, removed, outfitId)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchDownloadFileIndividually: url => dispatch(downloadFileIndividually(url)),
  dispatchPublishUnpublishOutfit: (campaignId, outfitId, deliverableId, type) =>
    dispatch(publishUnpublishOutfit(campaignId, outfitId, deliverableId, type)),
  dispatchDeclineOutfit: (campaignId, outfitId, deliverableId, reason) =>
    dispatch(declineOutfit(campaignId, outfitId, deliverableId, reason))
});

export default connect(mapStateToProps, mapDispatchToProps)(OutfitCreateModal);
